import React, { useEffect, useState } from "react";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import moment from "moment";

import caseStates from "@_configs/caseStates.config";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DateInput from "@_components/DateInput/DateInput";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import styles from "./CasesReportPopup.module.css";
import { useDispatch, useSelector } from "react-redux";
import { customerActions } from "@_actions/customer.actions";
import { catchError } from "@_utils/helpers";

function CasesReportPopup(props) {
  const dispatch = useDispatch();
  const sidebarReducer = useSelector((state) => state.sidebarReducer);
  const customerReducer = useSelector((state) => state.customerReducer);
  const { snackbarProps, showSnackbarError } = useSnackbar();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [caseState, setCaseState] = useState(sidebarReducer.status || "CASES");
  const [fieldsExpanded, setFieldsExpanded] = useState(false);
  const [checkboxes, setCheckboxes] = useState({
    case_id: true,
    source_case_id: true,
    domain: true,
    country: true,
    case_creation_date: true,
    activation_date: true,
    case_state: true,
    state_change_date: true,
  });

  useEffect(() => {
    setCaseState(sidebarReducer.status || "CASES");
  }, [sidebarReducer.status]);

  useEffect(() => {
    if (customerReducer.status.CASES_REPORT === "success") {
      dispatch(customerActions.clearStatusMessage("CASES_REPORT"));
      handleClose();
    } else if (catchError(customerReducer.status.CASES_REPORT)) {
      showSnackbarError(customerReducer.status.CASES_REPORT);
      dispatch(customerActions.clearStatusMessage("CASES_REPORT"));
    } else return;
    // eslint-disable-next-line
  }, [dispatch, customerReducer.status]);

  const handleClose = () => {
    setToDate("");
    setFromDate("");
    setCaseState(props.defaultCaseState || "CASES");
    setFieldsExpanded(false);
    setCheckboxes({
      case_id: true,
      source_case_id: true,
      domain: true,
      country: true,
      case_creation_date: true,
      activation_date: true,
      case_state: true,
      state_change_date: true,
    });
    props.close();
  };

  const handleCheckboxChange = (field) => {
    setCheckboxes((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleDownload = () => {
    const selectedFields = Object.keys(checkboxes).filter(
      (key) => checkboxes[key],
    );

    const reportData = {
      filter: {},
      fields: selectedFields,
      output_format: "excel",
    };

    if (fromDate) {
      reportData.filter.start_date = moment(fromDate).format("YYYY-MM-DD");
    }

    if (toDate) {
      reportData.filter.end_date = moment(toDate).format("YYYY-MM-DD");
    }
    if (caseState && caseState !== "CASES") {
      reportData.filter.case_state = caseState;
    }

    dispatch(customerActions.getCasesReport(reportData));
  };

  return (
    <DialogPopup
      title="Download case report"
      open={props.open}
      onClose={handleClose}
    >
      <div className={styles.content_container}>
        {/* Case Creation Date Section */}
        <div className={styles.section}>
          <h4 className={styles.section_title}>Case creation date</h4>
          <div className={styles.date_inputs}>
            <div className={styles.date_input_wrapper}>
              <DateInput
                label="From"
                value={fromDate}
                onChange={(value) => setFromDate(value)}
                className={styles.date_input}
              />
            </div>
            <div className={styles.date_input_wrapper}>
              <DateInput
                label="To"
                value={toDate}
                onChange={(value) => setToDate(value)}
                className={styles.date_input}
              />
            </div>
          </div>
          <p className={styles.description}> Dates in UTC</p>
        </div>

        <div className={styles.section}>
          <h4 className={styles.section_title}>Case state</h4>
          <FormControl className={styles.dropdown}>
            <Select
              variant="outlined"
              color="default"
              value={caseState}
              onChange={(e) => {
                setCaseState(e.target.value);
              }}
            >
              {Object.values(caseStates).map((state) => (
                <MenuItem key={state.key} value={state.key}>
                  {state.key === "CASES" ? "All" : state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className={styles.section}>
          <div className={styles.fields_header}>
            <h4 className={styles.section_title} style={{ margin: 0 }}>
              Fields
            </h4>
            <IconButton
              onClick={() => setFieldsExpanded(!fieldsExpanded)}
              className={styles.expand_button}
              size="large"
            >
              {fieldsExpanded ? (
                <ExpandMoreIcon />
              ) : (
                <ArrowForwardIosIcon fontSize="small" />
              )}
            </IconButton>
          </div>

          {fieldsExpanded && (
            <div className={styles.checkboxes_container}>
              <div className={styles.checkbox_row}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.case_id}
                      onChange={() => handleCheckboxChange("case_id")}
                    />
                  }
                  label="Case ID"
                  className={styles.checkbox}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.source_case_id}
                      onChange={() => handleCheckboxChange("source_case_id")}
                    />
                  }
                  label="External case ID"
                  className={styles.checkbox}
                />
              </div>
              <div className={styles.checkbox_row}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.domain}
                      onChange={() => handleCheckboxChange("domain")}
                    />
                  }
                  label="Domain"
                  className={styles.checkbox}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.country}
                      onChange={() => handleCheckboxChange("country")}
                    />
                  }
                  label="Country"
                  className={styles.checkbox}
                />
              </div>
              <div className={styles.checkbox_row}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.case_creation_date}
                      onChange={() =>
                        handleCheckboxChange("case_creation_date")
                      }
                    />
                  }
                  label="Creation date"
                  className={styles.checkbox}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.activation_date}
                      onChange={() => handleCheckboxChange("activation_date")}
                    />
                  }
                  label="Activation date"
                  className={styles.checkbox}
                />
              </div>
              <div className={styles.checkbox_row}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.case_state}
                      onChange={() => handleCheckboxChange("case_state")}
                    />
                  }
                  label="State"
                  className={styles.checkbox}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.state_change_date}
                      onChange={() => handleCheckboxChange("state_change_date")}
                    />
                  }
                  label="State Change date"
                  className={styles.checkbox}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <AppSnackbar {...snackbarProps} />
      <div className="action_container" style={{ paddingInlineEnd: "20px" }}>
        <Button className="btn_cancel" onClick={handleClose}>
          Cancel
        </Button>
        <Button className="btn_confirm" onClick={handleDownload}>
          Download
        </Button>
      </div>
    </DialogPopup>
  );
}

export default CasesReportPopup;
